import React, { useState, useEffect } from 'react';

function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '16px 0' }}>
      <svg width="32" height="32" viewBox="0 0 50 50">
        <circle cx="25" cy="25" r="20" fill="none" stroke="#2563eb" strokeWidth="5" strokeDasharray="31.4 31.4" strokeLinecap="round">
          <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
        </circle>
      </svg>
    </div>
  );
}

// necessary for dev 

let DEV_BACKEND_URL = ""
if (process.env.REACT_APP_BASE_URL) {
  DEV_BACKEND_URL = process.env.REACT_APP_BASE_URL;
}

const APP_VERSION = process.env.REACT_APP_VERSION || 'dev';

const translations = {
  en: {
    title: 'Manual Email Submission',
    dropzone: 'Drag & drop a <b>.msg</b> or <b>.eml</b> file here,<br />or <span style="text-decoration: underline; color: #2563eb;">click to select</span>',
    uploading: 'Uploading',
    selectedFile: 'Selected file:',
    uploadSuccess: 'Upload successful!',
    uploadSuccessDetail: 'We will analyze your message and get back to you within 2 business hours.',
    uploadFailed: 'Upload failed.',
    invalidLink: 'This link is invalid or has expired.',
  },
  de: {
    title: 'Manuelle E-Mail-Übermittlung',
    dropzone: '<b>.msg</b> oder <b>.eml</b> Datei hierher ziehen,<br />oder <span style="text-decoration: underline; color: #2563eb;">hier klicken</span>',
    uploading: 'Wird hochgeladen',
    selectedFile: 'Ausgewählte Datei:',
    uploadSuccess: 'Upload erfolgreich!',
    uploadSuccessDetail: 'Wir werden Ihre Nachricht analysieren und uns innerhalb von 2 Arbeitsstunden bei Ihnen melden.',
    uploadFailed: 'Upload fehlgeschlagen.',
    invalidLink: 'Dieser Link ist ungültig oder abgelaufen.',
  },
  fr: {
    title: 'Soumission manuelle d\'e-mail',
    dropzone: 'Glissez-déposez un fichier <b>.msg</b> ou <b>.eml</b> ici,<br />ou <span style="text-decoration: underline; color: #2563eb;">cliquez pour sélectionner</span>',
    uploading: 'Téléchargement en cours',
    selectedFile: 'Fichier sélectionné:',
    uploadSuccess: 'Téléchargement réussi!',
    uploadSuccessDetail: 'Nous analyserons votre message et vous répondrons dans les 2 heures ouvrables.',
    uploadFailed: 'Échec du téléchargement.',
    invalidLink: 'Ce lien est invalide ou a expiré.',
  },
  it: {
    title: 'Invio manuale di e-mail',
    dropzone: 'Trascina e rilascia un file <b>.msg</b> o <b>.eml</b> qui,<br />o <span style="text-decoration: underline; color: #2563eb;">clicca per selezionare</span>',
    uploading: 'Caricamento in corso',
    selectedFile: 'File selezionato:',
    uploadSuccess: 'Caricamento riuscito!',
    uploadSuccessDetail: 'Analizzeremo il tuo messaggio e ti risponderemo entro 2 ore lavorative.',
    uploadFailed: 'Caricamento non riuscito.',
    invalidLink: 'Questo link non è valido o è scaduto.',
  },
};

function getQueryParam(name) {
  return new URLSearchParams(window.location.search).get(name);
}

function App() {
  const [file, setFile] = useState(null);
  const [linkToken, setLinkToken] = useState(null);
  const [status, setStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [tokenMode, setTokenMode] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);
  const [language, setLanguage] = useState('en');
  
  const t = translations[language] || translations.en;

  useEffect(() => {
    const token = getQueryParam('token');
    if (token) {
      setLinkToken(token);
      setTokenMode(true);
      fetch(`${DEV_BACKEND_URL}/api/verify-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      }).then(async res => {
        if (!res.ok) throw new Error('Invalid or expired link.');
        const data = await res.json();
        if (data.reporter_language) {
          setLanguage(translations[data.reporter_language] ? data.reporter_language : 'en');
        }
      })
        .catch(() => {
          setTokenValid(false);
          setStatus({ type: 'error', key: 'invalidLink' });
        });
    } else {
      setStatus({ type: 'error', key: 'invalidLink' });
    }
  }, []);

  const uploadFile = async (fileToUpload) => {
    if (!fileToUpload) return;
    setFile(fileToUpload);
    setStatus(null);
    setUploading(true);
    const formData = new FormData();
    formData.append('file', fileToUpload);
    if (linkToken) formData.append('token', linkToken);
    try {
      const response = await fetch(`${DEV_BACKEND_URL}/api/upload`, {
        method: 'POST',
        body: formData,
      });
      if (response.ok) {
        setStatus({ type: 'success', key: 'uploadSuccess' });
      } else {
        setStatus({ type: 'error', key: 'uploadFailed' });
      }
    } catch (err) {
      setStatus({ type: 'error', key: 'uploadFailed' });
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      uploadFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      uploadFile(e.target.files[0]);
    }
  };

  // Disable interactions if token is missing or invalid
  const disableFileAndUpload = !tokenMode || !tokenValid;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e0e7ef 100%)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      fontFamily: 'Inter, Roboto, Arial, sans-serif',
      padding: 0,
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 18,
        boxShadow: '0 8px 32px rgba(30,41,59,0.13)',
        padding: '40px 32px 32px 32px',
        minWidth: 340,
        maxWidth: 420,
        width: '100%',
        margin: 16,
        position: 'relative',
      }}>
        {/* Logo/Branding */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 18 }}>
          <img height="67" src="./logo.png" alt="advact logo"/>
          {/* <span style={{ fontWeight: 900, fontSize: 22, color: '#22223b', marginLeft: 10, letterSpacing: '-1px' }}>advact</span> */}
        </div>
        <h2 style={{ textAlign: 'center', color: '#22223b', marginBottom: 18, fontWeight: 800, letterSpacing: '-1px', fontSize: 22 }}>{t.title}</h2>
        
        <div
          onDrop={disableFileAndUpload ? undefined : handleDrop}
          onDragOver={disableFileAndUpload ? undefined : (e => e.preventDefault())}
          style={{
            border: file ? '2.5px solid #2563eb' : '2.5px dashed #b6bbc7',
            background: file ? '#f1f5fb' : '#f8fafc',
            color: '#22223b',
            borderRadius: 14,
            padding: 36,
            margin: '22px 0 18px 0',
            textAlign: 'center',
            cursor: disableFileAndUpload ? 'not-allowed' : 'pointer',
            fontWeight: 500,
            fontSize: 18,
            transition: 'background 0.2s, border 0.2s',
            boxShadow: file ? '0 2px 8px rgba(30,41,59,0.08)' : 'none',
            outline: 'none',
            opacity: tokenValid ? 1 : 0.5,
          }}
          tabIndex={0}
          onClick={() => !disableFileAndUpload && !uploading && document.getElementById('fileInput').click()}
          onKeyDown={e => { if (!disableFileAndUpload && !uploading && (e.key === 'Enter' || e.key === ' ')) document.getElementById('fileInput').click(); }}
        >
          {uploading ? (
            <div>{t.uploading} <span style={{ color: '#2563eb', fontWeight: 600 }}>{file?.name}</span>...</div>
          ) : file ? (
            <div>{t.selectedFile} <span style={{ color: '#2563eb', fontWeight: 600 }}>{file.name}</span></div>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: t.dropzone }} />
          )}
          <input id="fileInput" type="file" accept=".msg,.eml" onChange={handleFileChange} style={{ display: 'none' }} disabled={disableFileAndUpload || uploading} />
        </div>
        {uploading && <Spinner />}
        {status && (
          <div style={{ marginTop: 24, textAlign: 'center' }}>
            <div style={{ color: status.type === 'error' ? '#cd393e' : status.type === 'success' ? '#06d6a0' : '#495057', fontWeight: 600, fontSize: 16 }}>
              {status.key ? t[status.key] : status.message}
            </div>
            {status.type === 'success' && (
              <div style={{ color: '#495057', fontSize: 14, marginTop: 8 }}>
                {t.uploadSuccessDetail}
              </div>
            )}
          </div>
        )}
      </div>
      <div style={{
        position: 'fixed',
        bottom: 8,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'space-between',
        padding: '0 12px',
        fontSize: 11,
        color: '#9ca3af',
      }}>
        <span>Service provided by advact AG, Switzerland</span>
        <span style={{ fontFamily: 'monospace' }}>v{APP_VERSION}</span>
      </div>
    </div>
  );
}

export default App;
